import { useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery } from 'react-query'
import { lettersApi } from '@/lib/api'
import { format } from 'date-fns'

export default function LetterDetailPage() {
  const { id } = useParams<{ id: string }>()

  const {
    data: letter,
    isLoading,
    error,
  } = useQuery(['letter', id], () => lettersApi.get(id!), {
    enabled: !!id,
    // Poll every 3s while OCR is processing, stop when done
    refetchInterval: (data) =>
      data?.ocr_status === 'pending' || data?.ocr_status === 'processing' ? 3000 : false,
  })

  const [downloadError, setDownloadError] = useState(false)

  if (isLoading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="animate-pulse">
          <div className="h-8 bg-olive-100 rounded w-1/2 mb-4"></div>
          <div className="h-4 bg-olive-100 rounded w-1/4 mb-8"></div>
          <div className="space-y-3">
            <div className="h-4 bg-olive-100 rounded"></div>
            <div className="h-4 bg-olive-100 rounded"></div>
            <div className="h-4 bg-olive-100 rounded w-3/4"></div>
          </div>
        </div>
      </div>
    )
  }

  if (error || !letter) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-12">
        <div className="text-center">
          <p className="text-red-600 mb-4">Letter not found</p>
          <Link to="/letters" className="btn-secondary">
            Back to Archive
          </Link>
        </div>
      </div>
    )
  }

  const handleDownload = async () => {
    setDownloadError(false)
    try {
      const blob = await lettersApi.download(letter.id)
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = letter.title || 'letter'
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    } catch {
      setDownloadError(true)
    }
  }

  return (
    <div className="max-w-4xl mx-auto px-4 py-12">
      <div className="mb-6">
        <Link to="/letters" className="text-olive-600 hover:text-olive-800">
          &larr; Back to Archive
        </Link>
      </div>

      <div className="card">
        <h1 className="text-3xl font-bold text-olive-900 mb-4">{letter.title}</h1>

        <div className="flex flex-wrap gap-4 text-sm text-olive-600 mb-6">
          {letter.sender_info?.name && (
            <div>
              <span className="font-medium">From:</span> {letter.sender_info.name}
            </div>
          )}
          {letter.recipient_info?.name && (
            <div>
              <span className="font-medium">To:</span> {letter.recipient_info.name}
            </div>
          )}
          <div>
            <span className="font-medium">Date:</span> {format(new Date(letter.created_at), 'PPP')}
          </div>
          <div>
            <span className="font-medium">Views:</span> {letter.view_count}
          </div>
        </div>

        {letter.tags.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-6">
            {letter.tags.map((tag) => (
              <span
                key={tag}
                className="px-3 py-1 bg-olive-100 text-olive-800 rounded-full text-sm"
              >
                {tag}
              </span>
            ))}
          </div>
        )}

        {/* OCR processing indicator */}
        {(letter.ocr_status === 'pending' || letter.ocr_status === 'processing') && (
          <div className="flex items-center gap-3 p-4 bg-olive-50 rounded-lg mb-6">
            <div className="animate-spin h-5 w-5 border-2 border-olive-600 border-t-transparent rounded-full" />
            <span className="text-olive-700">Extracting text from your letter...</span>
          </div>
        )}

        {/* OCR failed indicator */}
        {letter.ocr_status === 'failed' && (
          <div className="p-4 bg-red-50 text-red-700 rounded-lg mb-6">
            Text extraction failed. The letter is still saved.
          </div>
        )}

        {/* Extracted text (OCR content) - primary reading content */}
        {letter.ocr_status === 'completed' && letter.extracted_text && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-olive-900 mb-3">Letter Text</h2>
            {letter.ocr_confidence !== undefined && letter.ocr_confidence !== null && (
              <p className="text-xs text-olive-500 mb-2">
                OCR confidence: {Math.round(letter.ocr_confidence * 100)}%
              </p>
            )}
            <div className="prose prose-olive max-w-none bg-olive-50 p-6 rounded-lg">
              <p className="whitespace-pre-wrap" dir="auto">
                {letter.extracted_text}
              </p>
            </div>
          </div>
        )}

        {/* Manual content if present */}
        {letter.content && !letter.extracted_text && (
          <div className="prose prose-olive max-w-none mb-6">
            <p className="whitespace-pre-wrap">{letter.content}</p>
          </div>
        )}

        {/* Original file */}
        {letter.file_path && (
          <div className="border-t border-olive-100 pt-6">
            <h3 className="text-lg font-semibold text-olive-900 mb-3">Original File</h3>
            {letter.file_type?.startsWith('image/') ? (
              <img
                src={letter.file_path}
                alt={letter.title}
                className="max-w-full h-auto rounded-lg"
              />
            ) : (
              <div className="flex items-center justify-between p-4 bg-olive-50 rounded-lg">
                <div>
                  <p className="font-medium text-olive-900">{letter.title}</p>
                  <p className="text-sm text-olive-600">
                    {letter.file_type}{' '}
                    {letter.file_size
                      ? `\u2022 ${(letter.file_size / 1024 / 1024).toFixed(2)} MB`
                      : ''}
                  </p>
                </div>
                <button onClick={handleDownload} className="btn-primary">
                  Download
                </button>
              </div>
            )}
          </div>
        )}

        <div className="flex justify-end space-x-4 mt-8 pt-6 border-t border-olive-100">
          {downloadError && (
            <p className="text-red-600 text-sm self-center">Download failed. Please try again.</p>
          )}
          <button onClick={handleDownload} className="btn-primary">
            Download Original
          </button>
        </div>
      </div>
    </div>
  )
}
